import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, type Release } from "@/lib/api";
import { REPOS, statusVariant } from "@/lib/releases";

function ReleaseCard({
  release,
  onToggleEnabled,
  onDownloadAsset,
  isDownloadPending,
}: {
  release: Release;
  onToggleEnabled: (id: string, enabled: boolean) => void;
  onDownloadAsset: (releaseId: string, assetId: string) => void;
  isDownloadPending: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-medium">
            {release.repo}@{release.tag_name}
          </CardTitle>
          {!release.enabled && <Badge variant="secondary">disabled</Badge>}
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={release.enabled}
            onChange={(e) => onToggleEnabled(release.id, e.target.checked)}
          />
          Enabled
        </label>
      </CardHeader>
      <CardContent>
        {release.assets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No assets on this release.</p>
        ) : (
          <ul className="divide-y">
            {release.assets.map((asset) => (
              <li key={asset.id} className="py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>{asset.name}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant(asset.status)}>{asset.status}</Badge>
                    {asset.status !== "downloaded" && (
                      <Button
                        variant="link"
                        className="h-auto p-0 text-xs"
                        disabled={isDownloadPending}
                        onClick={() => onDownloadAsset(release.id, asset.id)}
                      >
                        {asset.status === "failed" ? "Retry" : "Download"}
                      </Button>
                    )}
                  </div>
                </div>
                {asset.status === "failed" && asset.download_error && (
                  <p className="mt-1 text-xs text-destructive">{asset.download_error}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function Releases() {
  const queryClient = useQueryClient();

  const { data: releases, isLoading } = useQuery({
    queryKey: ["releases"],
    queryFn: api.listReleases,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["releases"] });

  const sync = useMutation({
    mutationFn: () => Promise.all(REPOS.map((repo) => api.syncReleases(repo))),
    onSuccess: invalidate,
  });

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.setReleaseEnabled(id, enabled),
    onSuccess: invalidate,
  });

  const downloadAsset = useMutation({
    mutationFn: ({ releaseId, assetId }: { releaseId: string; assetId: string }) =>
      api.downloadAsset(releaseId, assetId),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Releases</h1>
          <p className="text-sm text-muted-foreground">
            Fetch releases from GitHub — new assets download automatically and are ready to
            distribute to machines.
          </p>
        </div>
        <Button onClick={() => sync.mutate()} disabled={sync.isPending}>
          {sync.isPending ? "Fetching…" : "Fetch releases"}
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {releases?.length === 0 && (
        <p className="text-sm text-muted-foreground">No releases synced yet.</p>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {REPOS.map((repo) => {
          const repoReleases = releases?.filter((release) => release.repo === repo) ?? [];
          return (
            <div key={repo} className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">{repo}</h2>
              {repoReleases.length === 0 ? (
                <p className="text-sm text-muted-foreground">No releases synced yet.</p>
              ) : (
                repoReleases.map((release) => (
                  <ReleaseCard
                    key={release.id}
                    release={release}
                    onToggleEnabled={(id, enabled) => toggleEnabled.mutate({ id, enabled })}
                    onDownloadAsset={(releaseId, assetId) =>
                      downloadAsset.mutate({ releaseId, assetId })
                    }
                    isDownloadPending={downloadAsset.isPending}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
